import {
  bindThemeParamsCssVars,
  expandViewport,
  hideBackButton,
  init,
  isThemeParamsCssVarsBound,
  miniAppReady,
  mountBackButton,
  mountThemeParamsSync,
  retrieveRawInitData,
} from "@telegram-apps/sdk-react";
import { env } from "../env";

/**
 * Every call into the SDK is optional: outside Telegram (a desktop browser during
 * development) the launch parameters are missing and these throw — the app must still
 * render. `quiet` swallows both a synchronous throw and a rejected promise.
 */
function quiet(fn: () => unknown): void {
  try {
    const result = fn();
    if (result && typeof (result as PromiseLike<unknown>).then === "function") {
      void (result as Promise<unknown>).catch(() => undefined);
    }
  } catch {
    // Not running inside Telegram, or the client does not support this call.
  }
}

/**
 * Prepare the Telegram shell: theme parameters become `--tg-theme-*` CSS variables (the
 * stylesheet reads them), the viewport expands to full height, and the back button is
 * hidden because this app is a single screen with nothing to go back to.
 */
export function setupTelegram(): void {
  quiet(() => init());
  quiet(() => {
    mountThemeParamsSync();
    if (!isThemeParamsCssVarsBound()) bindThemeParamsCssVars();
  });
  quiet(() => expandViewport());
  quiet(() => {
    mountBackButton();
    hideBackButton();
  });
  quiet(() => miniAppReady());
}

interface LegacyWebApp {
  Telegram?: { WebApp?: { initData?: string } };
}

/**
 * The signed launch payload, in order of reliability: the SDK's parser, Telegram's own
 * global (index.html loads it), then a development override. `null` means "not inside
 * Telegram" — the UI shows the not-linked screen rather than pretending to sign in.
 */
export function readRawInitData(): string | null {
  try {
    const raw = retrieveRawInitData();
    if (raw) return raw;
  } catch {
    // No launch parameters in this environment.
  }
  const legacy = (window as unknown as LegacyWebApp).Telegram?.WebApp?.initData;
  if (legacy) return legacy;
  return env.VITE_DEV_INIT_DATA ?? null;
}
